'use client'

import { useEffect, useMemo, useRef, useState } from "react";
import StoreDetailCard, { StoreDetailCardProps } from "./StoreDetailCard"
import StoreCardShimmer from "@/components/ui/shimmerUi/StoreCardShimmer";
import { useGetAllStoreCityAPIQuery, useGetAllStoreLocationApiQuery } from "@/redux/rtkQueries/clientSideGetApis";
import { StoresLocationDataEntity } from "@/types/GetAllStoresLocationApiType";
import { useResolvedCity } from "@/hooks/useResolvedCity";
import { Button } from "@heroui/react";

interface StoreLocatorIndexProps{
    cityName?:string|null;
}

const StoreLocatorIndex = ({ cityName }: StoreLocatorIndexProps) => {

    const [selectedCity, setSelectedCity] = useState<null | string>(null);
    const cityTabsRef = useRef<HTMLDivElement>(null);
    const resolvedCity = useResolvedCity();

    const { data: GetAllStoreCityAPI } = useGetAllStoreCityAPIQuery();
    
    const CityList = useMemo(
        () =>
            Array.isArray(GetAllStoreCityAPI?.data) && GetAllStoreCityAPI.data?.length
                ? GetAllStoreCityAPI.data
                : [],
        [GetAllStoreCityAPI?.data]
    );

    const { data: GetAllStoreLocationApi, isFetching } = useGetAllStoreLocationApiQuery({ cityName: selectedCity });

    const tabStoreCount = (city: string, apiCount: number) => {
        if (
            city === selectedCity &&
            GetAllStoreLocationApi?.cityUsed === city &&
            typeof GetAllStoreLocationApi.total === "number"
        ) {
            return GetAllStoreLocationApi.total;
        }
        return apiCount;
    };

    const StoreDetails = GetAllStoreLocationApi?.data?.map((curr: StoresLocationDataEntity) => ({
        imageUrl: curr?.featuredImage?.url || ``,
        heading: curr?.clinicName || 'Clinic Name',
        time: curr?.openingTime || '',
        address: curr?.address || '',
        phoneNumber: curr?.phoneNumber || '',
        mapLink: curr?.googleMapLink || 'https://google.com/maps',
        virtualTourLink: curr?.virtualTourLink || '#',
        galleryImages:
            curr?.galleryImages?.map((img) => ({
                url: img?.url ?? "",
                alt_text: img?.alt || img?.title || "clinic gallery image",
            })).filter((item) => item.url) || [],
        stateRegion: curr?.stateRegion,
        city: curr?.city,
        alt_text:curr?.featuredImage?.alt || curr?.featuredImage?.title || "clinic image"
    }));

    useEffect(() => {
        const cityUsed = GetAllStoreLocationApi?.cityUsed;
        if (cityUsed) setSelectedCity(cityUsed);
        else if (cityName) setSelectedCity(cityName);
        else if (resolvedCity) setSelectedCity(resolvedCity);
        else setSelectedCity('');
    }, [cityName, resolvedCity, GetAllStoreLocationApi?.cityUsed]);

    useEffect(() => {
        if (!selectedCity || !cityTabsRef.current) return;
        const tabIndex = CityList.findIndex((row) => row.cityName === selectedCity);
        if (tabIndex < 0) return;
        const timer = setTimeout(() => {
            const scrollEl = cityTabsRef.current;
            if (!scrollEl) return;
            const tablist = scrollEl.querySelector('[role="tablist"]');
            const tabs = tablist?.querySelectorAll('[role="tab"]');
            const targetTab = tabs?.[tabIndex] as HTMLElement | undefined;
            if (!targetTab) return;
            if (scrollEl.scrollWidth <= scrollEl.clientWidth) return;
            const elRect = scrollEl.getBoundingClientRect();
            const tabRect = targetTab.getBoundingClientRect();
            const nextLeft = scrollEl.scrollLeft + (tabRect.left - elRect.left);
            scrollEl.scrollTo({ left: Math.max(0, nextLeft), behavior: "smooth" });
        }, 100);
        return () => clearTimeout(timer);
    }, [selectedCity, CityList]);

    return (
        <>
            <div className="space_from_side">
                <div className="mt-5 2xl:mt-15 space-y-[15px] md:space-y-10">
                    <div className="flex flex-col justify-between items-center gap-y-[25px]">
                        <h2 className="heading_text" id="location_section">Find a Clinic Near You</h2>
                        {CityList.length ? (
                            <div
                                ref={cityTabsRef}
                                className="w-full md:w-full max-md:-mx-4 max-md:px-4 max-md:overflow-x-auto max-md:overflow-y-hidden max-md:overscroll-x-contain max-md:[-webkit-overflow-scrolling:touch] max-md:pb-1 max-md:pr-1 md:overflow-visible"
                            >
                                <div
                                    role="tablist"
                                    aria-label="Select city"
                                    className="max-md:grid max-md:w-max max-md:grid-flow-col max-md:grid-rows-2 max-md:auto-cols-max max-md:gap-2 md:flex md:w-full md:flex-wrap md:gap-2.5 xl:gap-4 md:justify-center"
                                >
                                    {CityList.map((row) => {
                                        const city = row.cityName;
                                        const apiCount = Number(row.storeCount) || 0;
                                        const isSelected = selectedCity === city;
                                        const count = tabStoreCount(city, apiCount);
                                        return (
                                            <Button
                                                key={city}
                                                type="button"
                                                role="tab"
                                                aria-selected={isSelected}
                                                aria-label={`${city}, ${count} ${count === 1 ? "clinic" : "clinics"}`}
                                                onPress={() => setSelectedCity(city)}
                                                className={`max-md:shrink-0 cursor-pointer w-[130px] rounded-full border border-lightSliver py-[9.99px] px-[22.48px] text-sm font-medium capitalize transition-colors md:text-base/[25.6px] ${
                                                    isSelected
                                                        ? "bg-silverBlue text-white"
                                                        : "bg-white text-textf2"
                                                }`}
                                            >
                                                <span className="inline-flex items-baseline gap-1">
                                                    <span>{city}</span>
                                                    {(count && count>1)?<span
                                                        className={
                                                            isSelected
                                                                ? "font-semibold text-white/90"
                                                                : "font-semibold text-textf2/80"
                                                        }
                                                    >
                                                        ({count})
                                                    </span>:<></>}
                                                </span>
                                            </Button>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 2xl:gap-8">
                        {isFetching ? (
                            <>
                                <StoreCardShimmer />
                                <StoreCardShimmer />
                            </>
                        ) : (
                            StoreDetails?.map((curr: StoreDetailCardProps, index) => <StoreDetailCard {...curr} key={`store_card_${index + 1}`} />)
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default StoreLocatorIndex